// src/components/SaleReturn.jsx
import React, { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft, Check } from "lucide-react";
import { money } from "../../../components/utils/index";
import { fetchSale, fetchSaleItems } from "../SaleDetails/SaleDetails";
import { createReturn } from "../../../api/salesApi";
import { fetchWarehouses } from "../../../api/inventory";
import { useAlert } from "../../../context/AlertContext";
import Model from "../../../components/ui/Model";

/**
 * SaleReturn.jsx
 *
 * Props:
 *  - saleId: string
 *  - onBack: () => void
 */

export default function SaleReturn({ saleId, onBack }) {
  // Sale summary
  const [sale, setSale] = useState(null);
  const [loadingSale, setLoadingSale] = useState(true);

  // Items for return
  const [items, setItems] = useState([]);
  const [returnQtys, setReturnQtys] = useState({}); // { itemId: qty }
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [damaged, setDamaged] = useState({});

  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState("");
  const [confirm, setConfirm] = useState(false);

  const getPricePerBottle = (item) => {
    const casePack = Number(item?.product?.case_pack_qty || 1);
    return item.units === "case"
      ? Number(item.price_per_unit) / casePack
      : Number(item.price_per_unit);
  };


  // Total refund calculation
  const refundTotal = useMemo(() => {
    return items.reduce((acc, item) => {
      const qtyBottles = Number(returnQtys[item.id] || 0);
      const pricePerBottle = getPricePerBottle(item);
      return acc + qtyBottles * pricePerBottle;
    }, 0);
  }, [items, returnQtys]);

  const [refundAmount, setRefundAmount] = useState("");

  // Load sale and items
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoadingSale(true);
      try {
        let r = await fetchSale(saleId);
        r = {
          ...r,
          items: await fetchSaleItems({ saleId }).then((e) => e.data),
        };
        if (!alive) return;
        setSale(r);
        setItems(r.items || []);
        setReturnQtys(
          Object.fromEntries((r.items || []).map((i) => [i.id, 0]))
        );
        // fetch warehouses
        const w = await fetchWarehouses();
        if (alive) setWarehouses(w || []);
      } finally {
        setLoadingSale(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [saleId]);
  const { showAlert } = useAlert();

  const getSoldBottles = (item) => {
    const casePack = Number(item?.product?.case_pack_qty || 1);
    return item.units === "case"
      ? Number(item.quantity) * casePack
      : Number(item.quantity);
  };

  const handleQtyChange = (itemId, value) => {
    const item = items.find((i) => i.id === itemId);
    if (!item) return;

    const maxBottles = getSoldBottles(item);

    let qtyBottles = Number(value);
    if (qtyBottles < 0) qtyBottles = 0;
    if (qtyBottles > maxBottles) qtyBottles = maxBottles;

    setReturnQtys((prev) => ({
      ...prev,
      [itemId]: qtyBottles,
    }));
  };

  // Submit refund / return
  const handleSubmit = async () => {
    const refund = Number(refundAmount) || 0;
    const total = Number(sale?.totals?.total) || 0;
    const balance = Number(sale?.totals?.balance) || 0;
    const maxRefund = total - balance;

    if (refund < 0 || refund > maxRefund) {
      // invalid refund amount
      showAlert({ type: "warning", message: "Refund insufficiant." });
      return;
    } else if (!warehouseId) {
      showAlert({ type: "warning", message: "At least one warehouse is slected." });
      return;
    }
    const totalQuantity = items.reduce((sum, item) => sum + (returnQtys[item.id] || 0), 0);
    if (!totalQuantity) {
      showAlert({ type: "warning", message: "At least one item is slected." });
      return;
    }
    setSubmitting(true);
    try {
      const returnItems = items
        .map((i) => ({
          item_id: i.id,
          quantity: Number(returnQtys[i.id] || 0),
          damaged: damaged[i.id] || false,
        }))
        .filter((i) => i.quantity > 0);

      await createReturn({
        saleId,
        warehouseId,
        items: returnItems,
        notes,
        createRefund: true,
        refundAmount: refund
      });

      showAlert(`Return processed successfully. Refund: ${money(refundTotal)}`);
      if (onBack) onBack();
    } catch (err) {
      console.error(err);
      showAlert("Failed to process return: " + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingSale) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading sale...
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={onBack}
            className="rounded-xl border border-gray-300 px-3 py-2 shadow-sm hover:bg-gray-50"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">
            Process Sale Return
          </h1>
        </div>
        <div className="rounded-2xl border border-gray-300 bg-white shadow-sm p-4 mb-6">
          <label className="text-sm text-gray-500">Warehouse</label>
          <select
            value={warehouseId}
            onChange={(e) => setWarehouseId(e.target.value)}
            required
            className="mt-1 w-full border border-gray-300 rounded-xl px-3 py-2 shadow-sm focus:outline-none focus:ring-2"
          >
            <option value="">Select warehouse</option>
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
        </div>

        {/* Sale Summary */}
        <div className="rounded-2xl border border-gray-300 p-4 bg-white mb-6 shadow-sm grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <div className="text-xs text-gray-500 uppercase">Invoice</div>
            <div className="font-semibold">{sale?.invoice_no}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase">Total</div>
            <div className="font-semibold">{money(sale?.totals?.total)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase">Balance</div>
            <div className="font-semibold">{money(sale?.totals?.balance)}</div>
          </div>
        </div>
        <div className="inline-flex items-center w-full gap-2 rounded-lg border-l-4 border-amber-500 bg-amber-50 px-4 py-2 text-sm text-amber-700 font-medium shadow-sm mb-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M12 20c4.418 0 8-3.582 8-8s-3.582-8-8-8-8 3.582-8 8 3.582 8 8 8z"
            />
          </svg>
          <span>Items are to be entered in <b>Bottles</b> only</span>
        </div>
        {/* Items Table */}
        <div className="rounded-2xl border border-gray-300 bg-white shadow-sm overflow-x-auto mb-6">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Product
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Qty Sold
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Price/unit
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Total
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Return Qty (in <span className="text-red-500 font-bold">bottles</span>)
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Reason
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((item) => (
                <tr
                  key={item.id}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-4 py-3 text-sm">{item.product.name}</td>
                  <td className="px-4 py-3 text-sm"> {getSoldBottles(item)} Bottles
                    {item.units === "case" && ` (${item.quantity} case)`}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {money(item.price_per_unit)}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {money(item.total_price)}
                  </td>
                  {/* Return Qty + Damaged */}
                  <td className="px-4 py-3 text-sm gap-2">
                    <input
                      type="number"
                      min={0}
                      value={returnQtys[item.id] || 0}
                      onChange={(e) => handleQtyChange(item.id, e.target.value)}
                      className="w-20 border border-gray-300 rounded-xl px-2 py-1 shadow-sm"
                    /> Bottles

                  </td>
                  <td className="px-4 py-3 text-sm">
                    <label className="inline-flex items-center gap-1 text-xs">
                      <input
                        type="checkbox"
                        checked={damaged[item.id] || false}
                        onChange={(e) =>
                          setDamaged({
                            ...damaged,
                            [item.id]: e.target.checked,
                          })
                        }
                        className="rounded border-gray-300 text-rose-600 focus:ring-rose-500"
                      />
                      Expired / Damaged
                    </label>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Refund Summary & Notes */}
        <div className="rounded-2xl border border-gray-300 bg-white shadow-sm p-4 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-gray-500">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full mt-1 border border-gray-300 rounded-xl px-3 py-2 shadow-sm focus:outline-none focus:ring-2"
            />
          </div>
          <div className="flex justify-end w-full">
            <div className="w-full">
              <div className="text-sm text-gray-500">Total Refund</div>
              <input
                type="number"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                className="w-full border border-gray-300 rounded-xl px-4 py-2 shadow-sm"
              />
            </div>
          </div>
        </div>
        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            onClick={() => {
              if (parseFloat(refundAmount) < 0) {
                showAlert({ type: "warning", message: "Select at least one item to return." });
                return;
              } else if (!warehouseId) {
                showAlert({ type: "warning", message: "At least one warehouse is slected." });
                return;
              } else {
                setConfirm(true)
              }
            }}
            className="inline-flex items-center gap-2 bg-yellow-600 text-white px-6 py-2 rounded-xl shadow-sm hover:bg-yellow-700 disabled:opacity-50"
          >
            Process Return
          </button>
        </div>
        <Model title={"Are you sure you want to refund?"} isOpen={confirm} onClose={setConfirm} noButtons={true}>
          <div>
            <div className="font-semibold text-lg text-red-500 mb-6">{money(refundAmount)}</div>
            {/* Submit Button */}
            <div className="flex justify-end">
              <button
                disabled={submitting}
                onClick={handleSubmit}
                className="inline-flex items-center gap-2 bg-red-600 text-white px-6 py-2 rounded-xl shadow-sm hover:bg-red-700 disabled:opacity-50"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                Confirm
              </button>
            </div></div>
        </Model>

      </motion.div>
    </div>
  );
}
